// Client-side business-hours / holiday evaluation.
// Ported from the SDK (test-app/Simplify360Chat.js) so the React app can
// compute `configDetails.offline` itself instead of trusting the cached value.

const convertClientTime = (configTimezone) => {
  const date = new Date();
  if (!configTimezone) {
    return date;
  }
  try {
    return new Date(date.toLocaleString("en-US", { timeZone: configTimezone }));
  } catch (e) {
    // Invalid/unsupported timezone — fall back to local time.
    return date;
  }
};

const isOfflineTime = (config, convertedTime) => {
  const holidays = config?.holidays ?? [];
  const workingHours = config?.workingHours ?? [];

  const isHoliday = holidays.find((holiday) => {
    const holidayDate = new Date(holiday?.day);
    holidayDate.setHours(0, 0, 0);

    const currentDate = new Date(convertedTime.valueOf());
    currentDate.setHours(0, 0, 0);

    return holidayDate.getTime() === currentDate.getTime();
  });

  const currentTime = new Date(convertedTime.valueOf());

  const constructCurrentDate = (time) => {
    const parts = (time || "0:0:0").split(":");
    return new Date(
      currentTime.getFullYear(),
      currentTime.getMonth(),
      currentTime.getDate(),
      parseInt(parts[0], 10),
      parseInt(parts[1], 10),
      parseInt(parts[2], 10)
    );
  };

  if (isHoliday) {
    const startTime = constructCurrentDate(isHoliday?.holiday_start);
    const endTime = constructCurrentDate(isHoliday?.holiday_end);

    if (currentTime >= startTime && currentTime <= endTime) {
      return true;
    }
  } else {
    const workingDayShifts = workingHours.filter(
      (workingDay) => workingDay.dayOfWeek === currentTime.getDay()
    );
    let isCurrentlyInShift = true;
    if (workingDayShifts.length) {
      for (const workingDayShift of workingDayShifts) {
        const workShiftStartTime = constructCurrentDate(workingDayShift?.workHourStart);
        const workShiftEndTime = constructCurrentDate(workingDayShift?.workHourEnd);

        isCurrentlyInShift =
          currentTime >= workShiftStartTime && currentTime <= workShiftEndTime;
        if (isCurrentlyInShift) {
          break;
        }
      }
    }

    return !isCurrentlyInShift;
  }

  return false;
};

// Returns the offline boolean for a given config.
// When business hours are not enabled, the backend-provided value is trusted.
const computeOffline = (config) => {
  const details = config?.configDetails;
  if (!details) {
    return false;
  }

  const hasSchedule =
    (Array.isArray(config?.holidays) && config.holidays.length > 0) ||
    (Array.isArray(config?.workingHours) && config.workingHours.length > 0);

  if (!details.enableBusinessHours || !hasSchedule) {
    return !!details.offline;
  }

  try {
    const convertedTime = convertClientTime(config?.timezone);
    return !!isOfflineTime(config, convertedTime);
  } catch (e) {
    return !!details.offline;
  }
};

export { computeOffline, convertClientTime, isOfflineTime };
